// src/store/location.js
import { defineStore } from 'pinia'
import { ref } from 'vue'
import axios from 'axios'
import { useLogger } from '@/composables/useLogger'

const logger = useLogger('location');
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL

export const useLocationStore = defineStore('location', () => {
  // ---------------- USER LOCATION ----------------
  const userAddress = ref({
    street: '',
    line2: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    formattedAddress: '',
  })
  const userCoords = ref(null) // { lat, lng } or null

  // ---------------- EQUIPMENT LOCATION ----------------
  const equipmentAddress = ref({
    street: '',
    line2: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    formattedAddress: '',
  })
  const equipmentCoords = ref(null) // { lat, lng } or null

  // ---------------- HELPERS ----------------
  function buildFullAddressText(addrObj) {
    if (!addrObj) return ''
    if (addrObj.formattedAddress) return addrObj.formattedAddress

    return [
      addrObj.street,
      addrObj.line2,
      addrObj.city,
      addrObj.state,
      addrObj.zipCode,
      addrObj.country,
    ]
      .filter(Boolean)
      .join(', ')
  }

  function shallowEqualAddress(a, b) {
    if (!a && !b) return true
    if (!a || !b) return false
    return (
      a.street === b.street &&
      a.line2 === b.line2 &&
      a.city === b.city &&
      a.state === b.state &&
      a.zipCode === b.zipCode &&
      a.country === b.country &&
      a.formattedAddress === b.formattedAddress
    )
  }

  function shallowEqualCoords(a, b) {
    if (!a && !b) return true
    if (!a || !b) return false
    return a.lat === b.lat && a.lng === b.lng
  }

  // ----- HYDRATE FROM BACKEND ADDRESS MODEL -----
  function hydrateUserFromAddressModel(address) {
    if (!address) {
      const nextAddress = {
        street: '',
        line2: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
        formattedAddress: '',
      }

      if (!shallowEqualAddress(userAddress.value, nextAddress)) {
        userAddress.value = nextAddress
      }
      if (!shallowEqualCoords(userCoords.value, null)) {
        userCoords.value = null
      }
      return
    }

    const nextAddress = {
      street: address.street_address || '',
      line2: address.street_address2 || '',
      city: address.city || '',
      state: address.state || '',
      zipCode: address.zip_code || '',
      country: address.country || '',
      formattedAddress:
        address.formatted_address || address.full_address || '',
    }

    const nextCoords =
      address.latitude !== null &&
      address.latitude !== undefined &&
      address.longitude !== null &&
      address.longitude !== undefined
        ? {
            lat: Number(address.latitude),
            lng: Number(address.longitude),
          }
        : null

    if (!shallowEqualAddress(userAddress.value, nextAddress)) {
      userAddress.value = nextAddress
    }
    if (!shallowEqualCoords(userCoords.value, nextCoords)) {
      userCoords.value = nextCoords
    }
  }

  function hydrateEquipmentFromAddressModel(address) {
    if (!address) {
      const nextAddress = {
        street: '',
        line2: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
        formattedAddress: '',
      }

      if (!shallowEqualAddress(equipmentAddress.value, nextAddress)) {
        equipmentAddress.value = nextAddress
      }
      if (!shallowEqualCoords(equipmentCoords.value, null)) {
        equipmentCoords.value = null
      }
      return
    }

    const nextAddress = {
      street: address.street_address || '',
      line2: address.street_address2 || '',
      city: address.city || '',
      state: address.state || '',
      zipCode: address.zip_code || '',
      country: address.country || '',
      formattedAddress:
        address.formatted_address || address.full_address || '',
    }

    const nextCoords =
      address.latitude !== null &&
      address.latitude !== undefined &&
      address.longitude !== null &&
      address.longitude !== undefined
        ? {
            lat: Number(address.latitude),
            lng: Number(address.longitude),
          }
        : null

    if (!shallowEqualAddress(equipmentAddress.value, nextAddress)) {
      equipmentAddress.value = nextAddress
    }
    if (!shallowEqualCoords(equipmentCoords.value, nextCoords)) {
      equipmentCoords.value = nextCoords
    }
  }

  // ----- BACKEND GEOCODING (fallback when coords missing) -----
  async function geocodeAddress(addressText) {
    if (!addressText) return null

    try {
      const { data } = await axios.get(
        `${apiBaseUrl}/api/locations/geocode/`,
        {
          params: { address: addressText },
          withCredentials: true,
        }
      )

      return {
        lat: data.lat,
        lng: data.lng,
        formatted_address: data.formatted_address,
        place_id: data.place_id,
        raw: data,
      }
    } catch (error) {
      logger.error('Failed to geocode address', error)
      return null
    }
  }

  async function ensureUserCoords() {
    if (userCoords.value) return userCoords.value

    const text = buildFullAddressText(userAddress.value)
    if (!text) return null

    const geo = await geocodeAddress(text)
    if (geo) {
      const nextCoords = { lat: geo.lat, lng: geo.lng }
      if (!shallowEqualCoords(userCoords.value, nextCoords)) {
        userCoords.value = nextCoords
      }
      if (!userAddress.value.formattedAddress && geo.formatted_address) {
        userAddress.value = {
          ...userAddress.value,
          formattedAddress: geo.formatted_address,
        }
      }
    }
    return userCoords.value
  }

  async function ensureEquipmentCoords() {
    if (equipmentCoords.value) return equipmentCoords.value

    const text = buildFullAddressText(equipmentAddress.value)
    if (!text) return null

    const geo = await geocodeAddress(text)
    if (geo) {
      const nextCoords = { lat: geo.lat, lng: geo.lng }
      if (!shallowEqualCoords(equipmentCoords.value, nextCoords)) {
        equipmentCoords.value = nextCoords
      }
      if (
        !equipmentAddress.value.formattedAddress &&
        geo.formatted_address
      ) {
        equipmentAddress.value = {
          ...equipmentAddress.value,
          formattedAddress: geo.formatted_address,
        }
      }
    }
    return equipmentCoords.value
  }

  // ----- NEW: explicit setter used by Google Places autocomplete -----
  function setEquipmentCoords(lat, lng, formatted = null) {
    if (lat == null || lng == null) {
      if (!shallowEqualCoords(equipmentCoords.value, null)) {
        equipmentCoords.value = null
      }
      return
    }

    const nextCoords = { lat: Number(lat), lng: Number(lng) }
    if (!shallowEqualCoords(equipmentCoords.value, nextCoords)) {
      equipmentCoords.value = nextCoords
    }

    if (formatted && !equipmentAddress.value.formattedAddress) {
      equipmentAddress.value = {
        ...equipmentAddress.value,
        formattedAddress: formatted,
      }
    }
  }

  // ----- NEW: alias used in the create form -----
  async function resolveEquipmentCoordsFromAddress() {
    // Just reuse the existing logic
    return await ensureEquipmentCoords()
  }

  // ----- DISTANCE (via backend Distance Matrix endpoint) -----
  async function distanceFromUserToEquipment(equipmentId = null) {
    const user = await ensureUserCoords()
    const equip = await ensureEquipmentCoords()

    if (!user || !equip) {
      logger.warn('Missing user or equipment coords for distance calculation', { user, equip })
      return null
    }

    try {
      const { data } = await axios.get(
        `${apiBaseUrl}/api/locations/distance-matrix/`,
        {
          params: {
            origin_lat: user.lat,
            origin_lng: user.lng,
            dest_lat: equip.lat,
            dest_lng: equip.lng,
            ...(equipmentId ? { equipment_id: equipmentId } : {}),
          },
          withCredentials: true,
        }
      )

      return {
        distance_text: data.distance_text,
        distance_meters: data.distance_meters,
        duration_text: data.duration_text,
        duration_seconds: data.duration_seconds,
      }
    } catch (error) {
      logger.error('Failed to calculate distance from user to equipment', error)
      return null
    }
  }

  return {
    // state
    userAddress,
    userCoords,
    equipmentAddress,
    equipmentCoords,

    // hydration
    hydrateUserFromAddressModel,
    hydrateEquipmentFromAddressModel,

    // coords + helpers
    ensureUserCoords,
    ensureEquipmentCoords,
    setEquipmentCoords,
    resolveEquipmentCoordsFromAddress,
    distanceFromUserToEquipment,
    buildFullAddressText,
  }
})
